<template>
	<div>
		<div class="loader" v-show="showLoading"></div>
		<div v-show="!showLoading">
			<div v-if="!isLockedForEdit">
				<button type="button" class="btn btn-primary m-0 mb-3" @click="showReport = true">{{ $t("report.newGeneratedDoc") }}</button>
				<button type="button" class="btn btn-primary m-0 ml-sm-3 mb-3" @click="showUpload = true">{{$t('uploadExistingDocs')}}</button>
				<button v-if="hasSigningCreatePermission"
                    type="button"
                    class="btn btn-primary m-0 ml-sm-3 mb-3"
                    @click.stop="createSigningClick">
                    <i class="fas fa-file-signature mr-2"></i>{{ $t('signing.createFromOrganizationalDocuments') }}
                </button>
                <button v-if="hasSigningCreatePermission"
                    type="button"
                    class="btn btn-primary m-0 ml-sm-3 mb-3"
                    @click.stop="createCoordinationClick">
                    <i class="fas fa-sitemap mr-2"></i>{{ $t('coordinations.createFromOrganizationalDocuments') }}
                </button>
			</div>

			<!-- documents grid -->
			<div class="loader" v-show="loading"></div>
			<div class="grid-wrapper" v-show="!loading">
				<kendo-datasource
					ref="datasource"
					:transport="transportSettings"
					:schema-model-id="'id'"
					:schema-total="'count'"
					:schema-data="'items'"
                    :schema-model-fields="schemaModelFields"
					:page-size="10"
					:server-paging="false"
					:server-sorting="false"
					:server-filtering="false"
					:change="onChange"
				></kendo-datasource>
				<kendo-grid
                    ref="docsGrid"
                    name="docsGrid"
                    :data-source-ref="'datasource'"
                    :navigatable="gridOptions.navigatable"
                    :editable="gridOptions.editable"
                    :filterable="gridOptions.filterable"
                    :pageable="gridOptions.pageable"
                    :reorderable="gridOptions.reorderable"
                    :resizable="gridOptions.resizable"
                    :groupable="gridOptions.groupable"
                    :allow-copy="gridOptions.allowCopy"
                    :column-menu="gridOptions.columnMenu"
                    :sortable-mode="gridOptions.sortable.mode"
                    :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                    :sortable-show-indexes="gridOptions.sortable.showIndexes"
                    :selectable="gridOptions.selectable"
                    :scrollable="gridOptions.scrollable"
                    :data-bound="onDatabound">
                    <kendo-grid-column
						title=" "
                        field="btnsColumn"
						:width="60"
						:lockable="false"
						:locked="false"
						:filterable="false"
						:sortable="false"
						:template="btnsTemplate"
					></kendo-grid-column>
                     <kendo-grid-column
                        :selectable="true"
                        :width="50"
                        :lockable="true"
                        :locked="false"

                    />
					<kendo-grid-column
                        field="entityId"
                        title="Id"
                        :width="100"
                        :lockable="true"
                        :locked="false"
                        ></kendo-grid-column>
					<kendo-grid-column
                        :title="$t('report.grid.isPublic')"
                        :lockable="true"
                        :locked="false"
                        :filterable="false"
						:sortable="false"
                        :template="toggleTemplate"
                        :width="120"
                    />
					<kendo-grid-column field="fileName" :title="$t('report.grid.title')" :width="400" :lockable="true" :locked="false"></kendo-grid-column>
					<kendo-grid-column field="type" :title="$t('report.grid.type')" :width="120" :lockable="true" :locked="false" :hidden="true"></kendo-grid-column>
					<kendo-grid-column field="createdBy" :title="$t('report.grid.generatedBy')" :width="200" :lockable="true" :locked="false"></kendo-grid-column>
					<kendo-grid-column
						field="createdOn"
						:title="$t('report.grid.generatedOn')"
						:width="200"
						:lockable="true"
						:locked="false"
						format="{0:dd.MM.yyyy H:mm}"
					></kendo-grid-column>
					<kendo-grid-column field="coordinationTitle" :title="$t('coordinations.pageTitle')" :width="200" :lockable="true" :locked="false"></kendo-grid-column>
					<kendo-grid-column field="coordinationResult" :title="$t('coordinations.result')" :width="200" :lockable="true" :locked="false"></kendo-grid-column>
					<kendo-grid-column field="coordinationStatusName" :title="$t('coordinations.status')" :width="200" :lockable="true" :locked="false"></kendo-grid-column>
					<kendo-grid-column field="coordinationCoordinators" :title="$t('coordinations.coordinators')" :width="200" :lockable="true" :locked="false"></kendo-grid-column>
					<kendo-grid-column field="signingTitle" :title="$tc('signing.pageTitle', 1)" :width="200" :lockable="true" :locked="false"></kendo-grid-column>
					<kendo-grid-column field="signingDate" :title="$t('signing.filesGirdHeaders.timestampUtc')" :format="customDateTimeFormat" :width="200" :lockable="true" :locked="false"></kendo-grid-column>
					<kendo-grid-column field="signatoryName" :title="$t('signing.filesGirdHeaders.signedBy')" :width="200" :lockable="true" :locked="false"></kendo-grid-column>
                    <kendo-grid-column
                        field="documentUploadTypeText"
                        :title="$t('documentsSettings.documentType')"
                        :width="220"
                        :lockable="true"
                        :locked="false"
                    />
				</kendo-grid>
			</div>
		</div>

		<ReportModal :show.sync="showReport" :generate="onGenerate" :initialValues="{EventId: eventId}"></ReportModal>
		<InfoModal :show.sync="showInfo" v-model="infoId"></InfoModal>
		<EventDocsUploadModal :show.sync="showUpload" @uploaded="onUploaded" :eventId="eventId"></EventDocsUploadModal>
		<CreateSignatureModal @onClose="onSigningModalClose" :selected-items="selectedItems" :event-id="eventId" :show="showCreateSignatureModal" @updated="refresh"></CreateSignatureModal>
        <CreateCoordinationModal @onClose="onCoordinationModalClose" :selected-items="selectedItems" :show="showCreateCoordinationModal" @updated="refresh"></CreateCoordinationModal>
	</div>
</template>

<script>
import Vue from 'vue';
import ReportModal from '@/components/reports/report.modal';
import FileService from '@/services/file.service';
import BtnsTemplate from '@/components/grid/StandardBtnsTemplate';
import ToggleTemplate from '@/components/grid/ToggleTemplate';
import InfoModal from '@/components/reports/info.modal';
import EventDocsUploadModal from '@/components/docs/EventDocsUpload.modal';
import CreateSignatureModal from '@/components/docs/createSignature.modal';
import CreateCoordinationModal from '@/components/docs/createCoordination.modal';
import { mapGetters } from 'vuex';
import { getDocumentUploadTypeText } from '@/common/documentUploadTypes';

export default {
	props: {
		eventId: {
			type: [String, Number],
			required: true
		},
        isLockedForEdit: {
            type: Boolean,
            default: false
        }
    },
	components: {
		ReportModal,
		InfoModal,
		EventDocsUploadModal,
        CreateSignatureModal,
        CreateCoordinationModal
	},
	data() {
		return {
			showReport: false,
			showLoading: false,
			showUpload: false,
			transportSettings: {
				read: {
					url: `${this.$store.getters.baseUrl}/api/events/${this.eventId}/docs`,
					dataType: 'json',
					type: 'GET',
					contentType: 'application/json',
					beforeSend: this.beforeSend
				}
			},
            schemaModelFields: {
                id: { name: 'id', from: 'id', type: 'number', editable: false, nullable: false, visible: false },
                fileName: { name: 'fileName', from: 'fileName', type: 'string', editable: true, nullable: true, visible: true },
                signingDate: { name: 'signingDate', type: 'date' },
				documentUploadType: { name: 'documentUploadType', type: 'string', editable: false, nullable: true, visible: true },
				documentUploadTypeText: { name: 'documentUploadTypeText', type: 'string', editable: false, nullable: true, visible: true }
            },
			showInfo: false,
			infoId: null,
			loading: false,
            showCreateSignatureModal: false,
            showCreateCoordinationModal: false,
            selectedItems: null,
            gridOptions: {
                ...this.$store.getters.gridOptions,
            },
		};
	},
	methods: {
		onDatabound(e) {
            const signingAllowFileTypes = this.signingAllowFileTypes;
            var items = e.sender.items();
            items.each(function (idx, item) {
                let dataItem = e.sender.dataItem(item);
                if (!signingAllowFileTypes.includes(dataItem.mimeType)) {
                    $(item).addClass('checkbox-disabled');
                }
            });
        },
		onChange(data) {
			// parse date field in the grid data
			data.items.forEach(item => {
				item.createdOn = new Date(item.createdOn);
				item.documentUploadTypeText = this.getDocumentUploadTypeText(item.documentUploadType);
			});
		},
		getDocumentUploadTypeText(code) {
			return getDocumentUploadTypeText(code, this.$i18n);
		},
		beforeSend: function(req) {
			if (this.token) {
				req.setRequestHeader('Authorization', 'Bearer ' + this.token);
			}
		},
		btnsTemplate: function(e) {
			return {
				template: Vue.component(BtnsTemplate.name, BtnsTemplate),
				templateArgs: {
					...e,
					btns: [
						{
							name: 'download',
							text: '',
							tooltip: this.$t('buttons.download.tooltipText'),
							class: 'btn btn-success',
							icon: 'fas fa-download',
							show: true,
							clickHandler: this.downloadDoc
						},
						{
							name: 'details',
							text: '',
							tooltip: this.$t('buttons.details.tooltipText'),
							class: 'btn btn-info',
							icon: 'fas fa-info-circle',
							show: e.reportId,
							clickHandler: this.showDetails
						},
						{
							name: 'remove',
							text: '',
							tooltip: this.$t('buttons.delete.tooltipText'),
							class: 'btn btn-danger',
							icon: 'fas fa-trash',
							show: this.claims.event.update && !this.isLockedForEdit,
							clickHandler: this.delete
						},
                        {
                            name: 'details',
                            text: '',
                            tooltip: this.$tc('signing.pageTitle', 1),
                            class: 'btn btn-info',
                            icon: 'fas fa-file-signature',
                            show: this.claims.tasks.read && !!e.signingId,
                            clickHandler: this.signingDetailsBtnClick
                        },
                        {
                            name: 'details',
                            text: '',
                            tooltip: this.$tc('coordinations.pageTitle', 1),
                            class: 'btn btn-info',
                            icon: 'fas fa-sitemap',
                            show: this.claims.tasks.read && !!e.coordinationId,
                            clickHandler: this.coordinationDetailsBtnClick
                        },
					]
				}
			};
		},
        toggleTemplate: function(e) {
            return {
				template: Vue.component(ToggleTemplate.name, ToggleTemplate),
				templateArgs: {
					...e,
                    toggle: {
                        value: e.isPublic,
                        checkLabel: this.$t('yes'),
                        uncheckLabel: this.$t('no'),
                        name: '',
                        disabled: false,
                        checkHandller: this.onIsPublicChangeClick
                    }
				}
			};
        },
		downloadDoc(e) {
            const downloadUri = this.$store.getters.baseUrl + `/api/file/download/?id=${e.id}&type=${e.docTypeCode}&hash=${e.hash}`;
            this.loading = true;
            this.$http.get(downloadUri, { responseType: 'blob' })
                .then(response => {
                    const blob = new Blob([response.data]);
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', e.fileName); // or any other extension
                    document.body.appendChild(link);
                    link.click();
                    window.URL.revokeObjectURL(url);
                })
                .catch(err => {
                    this.$vs.notify({
                        color: 'danger',
                        title: this.$t('notifications.error'),
                        text: this.$helpers.errorText(err)
                    })
                })
                .then(() => { this.loading = false; });
		},
		showDetails(e) {
			this.infoId = e.id;
			this.showInfo = true;
		},
        signingDetailsBtnClick(e) {
            let routeData = this.$router.resolve({ name: 'signatures', query: { view: e.signingId } });
            window.open(routeData.href, '_blank');
        },
        coordinationDetailsBtnClick(e) {
            let routeData = this.$router.resolve({ name: 'coordinations', query: { view: e.coordinationId } });
            window.open(routeData.href, '_blank');
        },
		delete(e) {
			this.$vs.dialog({
				type: 'confirm',
				color: 'danger',
				title: this.$t('buttons.delete.action'),
				text: this.$t('buttons.delete.confirmationText'),
				accept: () => this.deleteDoc(e.id),
				acceptText: this.$t('buttons.ok'),
				cancelText: this.$t('buttons.cancel')
			});
		},
		deleteDoc(id) {
			this.$http
				.delete(`/api/events/docs/${id}`)
				.then(() => {
					this.$vs.notify({
						color: 'success',
						title: this.$t('deleteSuccess'),
						text: ''
					});
					this.refresh();
				})
				.catch(err =>
					this.$vs.notify({
						color: 'danger',
						title: this.$t('notifications.error'),
						text: this.$t('notifications.serverError')
					})
				);
		},
		onGenerate(reportData) {
			this.showLoading = true;
			reportData.entityId = this.eventId;

			FileService.downloadFilePost(`/api/Reporting/generateOrganizationalDoc`, reportData)
				.then(() => this.refresh())
				.catch(err =>
					this.$vs.notify({
						color: 'danger',
						title: this.$t('notifications.error'),
						text: this.$helpers.errorText(err)
					})
				)
				.then(() => (this.showLoading = false));
		},
		onUploaded() {
			this.refresh();
		},
		refresh() {
			this.$refs['docsGrid'].kendoWidget().dataSource.read();
		},
        getGridSelectedRows() {
            const grid = this.$refs['docsGrid'];
            if (!grid) return null;
            const selectedItems = this.$helpers.getGridSelectedRows(grid.kendoWidget());
            return selectedItems;
        },
        createSigningClick() {
            const signingAllowFileTypes = this.signingAllowFileTypes;
            let selectedItems = this.getGridSelectedRows().filter(x => signingAllowFileTypes.includes(x.mimeType));

            if (!selectedItems || selectedItems.length <= 0) {
                return this.$vs.notify({
                    title: this.$t('signing.missingSelectedOrganizationalDocuments'),
                    text: '',
                    color: 'warning',
                    time: this.$store.getters.notificationDuration
                });
            }

			this.$vs.dialog({
				type: 'confirm',
				color: 'success',
				title: this.$t('buttons.create.action'),
				text: this.$t('signing.organizationalDocumentsSigningCreateConfirmation'),
				accept: () => this.createSigning(selectedItems),
				acceptText: this.$t('buttons.ok'),
				cancelText: this.$t('buttons.cancel')
			});
        },
        createSigning(selectedItems) {
            this.selectedItems = selectedItems;
            $('#createSigningFromOrgDocumentsModal').modal('show');
            this.showCreateSignatureModal = true;
        },
        onSigningModalClose() {
            this.selectedItems = null;
            this.showCreateSignatureModal = false;
        },
        onIsPublicChangeClick(checked, item) {
            this.$http
				.put(`/api/events/docs/toggleIsPublic?id=${item.organizationalDocumentId}&isPublic=${checked}`)
				.then(() => {
					this.$vs.notify({
						color: 'success',
						title: this.$t('updateSuccess'),
						text: ''
					});
					this.refresh();
				})
				.catch(err =>
					this.$vs.notify({
						color: 'danger',
						title: this.$t('notifications.error'),
						text: this.$t('notifications.serverError')
					})
				);
        },
        createCoordinationClick() {
            const signingAllowFileTypes = this.signingAllowFileTypes;
            let selectedItems = this.getGridSelectedRows().filter(x => signingAllowFileTypes.includes(x.mimeType));

            if (!selectedItems || selectedItems.length <= 0) {
                return this.$vs.notify({
                    title: this.$t('signing.missingSelectedOrganizationalDocuments'),
                    text: '',
                    color: 'warning',
                    time: this.$store.getters.notificationDuration
                });
            }

			this.$vs.dialog({
				type: 'confirm',
				color: 'success',
				title: this.$t('buttons.create.action'),
				text: this.$t('coordinations.organizationalDocumentsCoordinationCreateConfirmation'),
				accept: () => this.createCoordination(selectedItems),
				acceptText: this.$t('buttons.ok'),
				cancelText: this.$t('buttons.cancel')
			});
        },
        createCoordination(selectedItems) {
            this.selectedItems = selectedItems;
            $('#createCoordinationFromOrgDocumentsModal').modal('show');
            this.showCreateCoordinationModal = true;
        },
        onCoordinationModalClose() {
            this.selectedItems = null;
            this.showCreateCoordinationModal = false;
        },
	},
	computed: {
        ...mapGetters(['claims', 'userId', 'dateTimeFormat', 'signingAllowFileTypes']),
		token: function() {
			return this.$store.getters.authToken;
		},
        hasSigningCreatePermission() {
            return this.claims.tasks.create;
        },
        customDateTimeFormat() {
            return `{0: ${this.dateTimeFormat}}`;
        },
	},
	mounted() {}
};
</script>

<style>
    tr.checkbox-disabled  input.k-checkbox {
        display: none !important;
    }

    tr.checkbox-disabled  label.k-checkbox-label {
        display: none !important;
    }
</style>

